// src/component/HomeBigFeatureBanner.jsx
import React from "react";

/**
 * Большой баннер с картинкой слева и текстом справа.
 *
 * Props:
 * - image: string | import-url
 * - title?: string
 * - text?: string
 * - description?: React.ReactNode
 * - cta?: string
 * - href?: string
 * - imagePosition?: string       // 'center', '60% 50%', ...
 * - imageFit?: 'cover'|'contain'|'smart' // smart: фоновое cover + основное contain
 * - imageBg?: string             // фон под изображением (по умолчанию '#f8fafc')
 * - priority?: boolean           // true -> eager + fetchPriority=high
 * - className?: string           // внешние отступы
 */
export default function BigFeatureBanner({
  image,
  title,
  text,
  description,
  cta = "Подробнее",
  href,
  imagePosition = "center",
  imageFit = "cover",
  imageBg = "#f8fafc",
  priority = false,
  className = "",
}) {
  const src = typeof image === "string" ? image : image?.default || image?.src || "";

  return (
    <section className={`mt-10 max-w-7xl mx-auto px-4 ${className}`}>
      <div className="bg-white rounded-xl shadow-md overflow-hidden grid md:grid-cols-2">
        {/* Левая половина: изображение */}
        <div className="relative" style={{ background: imageBg }}>
          {/* Предсказуемая высота (адаптивная) */}
          <div className="w-full" style={{ height: "clamp(220px, 32vw, 440px)" }} />

          {src ? (
            imageFit === "smart" ? (
              // Умное вписывание: фон заполняет (cover, blur), основное фото целиком (contain)
              <div className="absolute inset-0">
                <img
                  src={src}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full"
                  style={{
                    objectFit: "cover",
                    objectPosition: imagePosition,
                    filter: "blur(12px)",
                    transform: "scale(1.06)", // чтобы края блюра не просвечивали
                    opacity: 0.7,
                  }}
                  loading={priority ? "eager" : "lazy"}
                  fetchPriority={priority ? "high" : "auto"}
                  decoding="async"
                />
                <img
                  src={src}
                  alt={title || "Изображение баннера"}
                  className="absolute inset-0 h-full w-full"
                  style={{ objectFit: "contain", objectPosition: imagePosition }}
                  loading={priority ? "eager" : "lazy"}
                  fetchPriority={priority ? "high" : "auto"}
                  decoding="async"
                  sizes="(min-width: 768px) 50vw, 100vw"
                />
                {/* лёгкая затемняющая вуаль для контраста */}
                <div className="absolute inset-0 pointer-events-none"
                     style={{ background: "linear-gradient(0deg, rgba(0,0,0,0.04), rgba(0,0,0,0.04))" }} />
              </div>
            ) : (
              <img
                src={src}
                alt={title || "Изображение баннера"}
                className="absolute inset-0 h-full w-full"
                style={{ objectFit: imageFit, objectPosition: imagePosition }}
                loading={priority ? "eager" : "lazy"}
                fetchPriority={priority ? "high" : "auto"}
                decoding="async"
                sizes="(min-width: 768px) 50vw, 100vw"
              />
            )
          ) : (
            <div className="absolute inset-0 bg-gray-100" />
          )}
        </div>

        {/* Правая половина: текст / CTA */}
        <div className="p-6 sm:p-8 flex flex-col justify-center">
          {title && <h2 className="text-2xl font-bold mb-2">{title}</h2>}

          {description ? (
            <div className="mb-4">{description}</div>
          ) : text ? (
            <p className="text-gray-600 text-sm mb-4">{text}</p>
          ) : null}

          {href && (
            <a
              href={href}
              className="w-fit bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-md text-sm transition"
            >
              {cta}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
